// Game sound effects. Most are procedural Web Audio API for instant
// playback with zero noise; the card shuffle / draw / place sounds use
// real recorded samples loaded from /game-assets/ for a higher-quality
// tactile feel. Those are routed through playSampleRandom().

import { getEffectiveSfxVolume } from '../arena/soundSettings';
import { getLocalApiOrigin } from '../localApi';

let ctx = null;
// Base level for the procedural sounds. The mixer (master x sfx) is applied
// on top in sfxLevel(), so this stays at 1 unless setVolume() lowers it.
let volume = 1;
let muted = false;

function getCtx() {
  if (!ctx) ctx = new AudioContext();
  // .catch: resume() rejects in browsers when called before the first
  // user gesture; the next play attempt resumes again anyway.
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

// --- Recorded sample playback ------------------------------------
// All samples decoded to AudioBuffers at preload time. Playing is
// createBufferSource → connect GainNode → start(). Standard game
// audio pattern. Same as uiSounds.js.

const sampleBuffers = new Map();

function getSampleUrl(filename) {
  return `${getLocalApiOrigin()}/game-assets/${filename}`;
}

async function preloadSample(filename) {
  if (sampleBuffers.has(filename)) return;
  sampleBuffers.set(filename, null);
  try {
    const resp = await fetch(getSampleUrl(filename));
    const ab = await resp.arrayBuffer();
    const decoded = await getCtx().decodeAudioData(ab);
    sampleBuffers.set(filename, decoded);
  } catch {
    sampleBuffers.delete(filename);
  }
}

function playSampleRandom(filenames, gain = 1) {
  if (muted) return;
  const sfxVol = getEffectiveSfxVolume();
  if (sfxVol <= 0) return;
  const filename = filenames[Math.floor(Math.random() * filenames.length)];
  const buffer = sampleBuffers.get(filename);
  if (!buffer) return;
  try {
    const ac = getCtx();
    const source = ac.createBufferSource();
    source.buffer = buffer;
    const g = ac.createGain();
    g.gain.value = Math.min(1, gain * sfxVol);
    source.connect(g).connect(ac.destination);
    source.start(0);
  } catch {}
}

const SHUFFLE_LIGHT_FILES = [
  'snd-card-shuffle-light-1.mp3',
  'snd-card-shuffle-light-2.mp3',
  'snd-card-shuffle-light-3.mp3',
];
const SHUFFLE_FILES = [
  'snd-card-shuffle-1.mp3',
  'snd-card-shuffle-3.mp3',
];
const DEAL_FILES = [
  'snd-card-deal-1.mp3',
  'snd-card-deal-2.mp3',
  'snd-card-deal-3.mp3',
];
const DEAL_SMALL_FILES = ['snd-card-deal-small-1.mp3'];
// Combat impacts, tiered by damage dealt: attack-N for N damage
// (attack-4 covers 4+), magic-N when the striker is a Spellcaster
// (magic-3 covers 3+). The overlay picks the key per strike beat.
const ATTACK_FILES = ['snd-attack-1.mp3', 'snd-attack-2.mp3', 'snd-attack-3.mp3', 'snd-attack-4.mp3'];
const MAGIC_FILES = ['snd-magic-1.mp3', 'snd-magic-2.mp3', 'snd-magic-3.mp3'];
// Lightning-strike effects (Sparkmage's shock). CC0 thunderclaps from
// freesound.org (sounds 436790 / 436794 by user roboroo).
const LIGHTNING_FILES = ['snd-lightning-impact-1.ogg', 'snd-lightning-impact-2.ogg', 'snd-lightning-impact-3.ogg'];
const lightningVoices = new Set();
let lightningTake = 0;
let lightningBus = null;
const THUNDER_FILES = ['snd-thunder-1.mp3', 'snd-thunder-2.mp3'];
// Scry confirm "swoosh." Played when a scry decision is committed
// (Done) or a scried card is summoned (Cradle of Etherrum). The raw
// clip is hot and ends abruptly, so it's attenuated and given a short
// linear fade-out tail rather than routed through playSampleRandom().
const SCRY_FILE = 'snd-scry.mp3';
const SCRY_GAIN = 0.4;
const SCRY_FADE_OUT = 0.45;

// Called by the VFX impact beat, not when its descending leader starts.
export function playLightningImpact({ variant = 'bolt', pan = 0 } = {}) {
  if (muted || getEffectiveSfxVolume() <= 0 || volume <= 0) return;
  const buffer = sampleBuffers.get(LIGHTNING_FILES[lightningTake++ % LIGHTNING_FILES.length]);
  if (!buffer) { playSampleRandom(THUNDER_FILES, .4); return; }
  try {
    const ac = getCtx();
    if (lightningVoices.size >= 4) {
      const oldest = lightningVoices.values().next().value;
      oldest.stop(); lightningVoices.delete(oldest);
    }
    if (!lightningBus) {
      lightningBus = ac.createDynamicsCompressor();
      lightningBus.threshold.value = -10; lightningBus.knee.value = 12;
      lightningBus.ratio.value = 6; lightningBus.attack.value = .003; lightningBus.release.value = .18;
      lightningBus.connect(ac.destination);
    }
    const source = ac.createBufferSource(); source.buffer = buffer;
    source.playbackRate.value = variant === 'storm' ? .88 : variant === 'sparkmage' ? 1.12 : 1;
    const gain = ac.createGain();
    gain.gain.value = sfxLevel(variant === 'storm' ? .7 : variant === 'sparkmage' ? .5 : .62);
    const panner = ac.createStereoPanner(); panner.pan.value = Math.max(-.7, Math.min(.7, pan));
    source.connect(gain).connect(panner).connect(lightningBus);
    lightningVoices.add(source);
    source.onended = () => { lightningVoices.delete(source); source.disconnect(); gain.disconnect(); panner.disconnect(); };
    source.start(ac.currentTime);
  } catch (error) { console.warn('[lightning] sound playback failed', error); }
}

function playScry() {
  if (muted) return;
  const sfxVol = getEffectiveSfxVolume();
  if (sfxVol <= 0) return;
  const buffer = sampleBuffers.get(SCRY_FILE);
  if (!buffer) return;
  try {
    const ac = getCtx();
    const now = ac.currentTime;
    const source = ac.createBufferSource();
    source.buffer = buffer;
    const g = ac.createGain();
    const peak = Math.min(1, SCRY_GAIN * sfxVol);
    const fade = Math.min(SCRY_FADE_OUT, buffer.duration);
    g.gain.setValueAtTime(peak, now);
    g.gain.setValueAtTime(peak, now + buffer.duration - fade);
    g.gain.linearRampToValueAtTime(0.0001, now + buffer.duration);
    source.connect(g).connect(ac.destination);
    source.start(0);
  } catch {}
}

function cardShuffleAtlas() { playSampleRandom(SHUFFLE_LIGHT_FILES); }
function cardShuffleSpellbook() { playSampleRandom(SHUFFLE_FILES); }
function cardPlaceSample() { playSampleRandom(DEAL_SMALL_FILES); }
function cardDrawSample() { playSampleRandom(DEAL_FILES); }

// Peak gain for a procedural sound: its own level, scaled by the mixer.
function sfxLevel(level) {
  return level * volume * getEffectiveSfxVolume();
}

function makeGain(ac, vol) {
  const g = ac.createGain();
  g.gain.value = sfxLevel(vol);
  g.connect(ac.destination);
  return g;
}

function whiteNoise(ac, duration) {
  const samples = ac.sampleRate * duration;
  const buffer = ac.createBuffer(1, samples, ac.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < samples; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buffer;
  return src;
}

// ─── Sound definitions ───

function cardPickup() {
  const ac = getCtx();
  const now = ac.currentTime;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(sfxLevel(0.3), now + 0.01);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.06);
  gain.connect(ac.destination);

  const filter = ac.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 3500;
  filter.Q.value = 0.8;
  filter.connect(gain);

  const noise = whiteNoise(ac, 0.06);
  noise.connect(filter);
  noise.start(now);
  noise.stop(now + 0.06);
}

function cardFlip() {
  const ac = getCtx();
  const now = ac.currentTime;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(sfxLevel(0.25), now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
  gain.connect(ac.destination);

  const filter = ac.createBiquadFilter();
  filter.type = 'bandpass';
  filter.frequency.value = 2200;
  filter.Q.value = 1.2;
  filter.connect(gain);

  const noise = whiteNoise(ac, 0.05);
  noise.connect(filter);
  noise.start(now);
  noise.stop(now + 0.05);
}

function diceRoll() {
  const ac = getCtx();
  const now = ac.currentTime;

  // Series of short impacts with decreasing energy
  for (let i = 0; i < 5; i++) {
    const t = now + i * 0.07 + Math.random() * 0.03;
    const energy = 1 - i * 0.18;
    const gain = ac.createGain();
    gain.gain.setValueAtTime(sfxLevel(0.25 * energy), t);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    gain.connect(ac.destination);

    const filter = ac.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1200 + Math.random() * 1500;
    filter.Q.value = 1.0;
    filter.connect(gain);

    const noise = whiteNoise(ac, 0.06);
    noise.connect(filter);
    noise.start(t);
    noise.stop(t + 0.06);
  }
}

function uiClick() {
  const ac = getCtx();
  const now = ac.currentTime;
  const gain = ac.createGain();
  gain.gain.setValueAtTime(sfxLevel(0.15), now);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.04);
  gain.connect(ac.destination);

  const osc = ac.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(1000, now);
  osc.frequency.exponentialRampToValueAtTime(600, now + 0.04);
  osc.connect(gain);
  osc.start(now);
  osc.stop(now + 0.04);
}

// ─── Public API ───

const SOUNDS = {
  cardPickup,
  cardFlip,
  diceRoll,
  uiClick,
  // Recorded samples. See playSampleRandom() above
  cardPlace: cardPlaceSample,
  cardDraw: cardDrawSample,
  cardShuffleSpellbook,
  cardShuffleAtlas,
  attack1: () => playSampleRandom([ATTACK_FILES[0]]),
  attack2: () => playSampleRandom([ATTACK_FILES[1]]),
  attack3: () => playSampleRandom([ATTACK_FILES[2]]),
  attack4: () => playSampleRandom([ATTACK_FILES[3]]),
  magic1: () => playSampleRandom([MAGIC_FILES[0]]),
  magic2: () => playSampleRandom([MAGIC_FILES[1]]),
  magic3: () => playSampleRandom([MAGIC_FILES[2]]),
  thunder: () => playSampleRandom(THUNDER_FILES, 0.65),
  scry: playScry,
};

/** Sound key for one combat strike: damage tier + striker's school. */
export function combatImpactKey(amount, magic) {
  const n = Math.max(1, Number(amount) || 1);
  return magic ? `magic${Math.min(3, n)}` : `attack${Math.min(4, n)}`;
}

export function playSound(name) {
  if (muted) return;
  const fn = SOUNDS[name];
  if (!fn) return;
  // The recorded samples re-check this themselves; the procedural sounds
  // rely on this gate so a disabled SFX channel never opens an AudioContext.
  if (getEffectiveSfxVolume() <= 0) return;
  fn();
}

export function setVolume(v) {
  volume = Math.max(0, Math.min(1, v));
}

export function setMuted(m) {
  muted = m;
}

export function isMuted() {
  return muted;
}

export function preloadSounds() {
  // AudioContext is created lazily on first procedural playSound. The
  // recorded samples DO benefit from preloading so the first shuffle /
  // draw / place doesn't have a network/decode hitch.
  for (const f of [
    ...SHUFFLE_LIGHT_FILES,
    ...SHUFFLE_FILES,
    ...DEAL_FILES,
    ...DEAL_SMALL_FILES,
    ...ATTACK_FILES,
    ...MAGIC_FILES,
    ...THUNDER_FILES,
    ...LIGHTNING_FILES,
    SCRY_FILE,
  ]) {
    preloadSample(f);
  }
}
